import { useEffect, useRef } from "react";
import ApiEngine from "util/ApiEngine";
import { ApiKey, ApiUrl } from "util/Constant";

const POLL_INTERVAL_MS = 60 * 1000;
const MAX_WAIT_MS = 24 * 60 * 60 * 1000;

/**
 * While enabled (logged in + active promo), sleep until promoExpiresAt from Redux,
 * then call PromoExpiryTick. Re-polls on waiting/retry until forfeited / none.
 * Does not tick on mount just to learn expiresAt — that comes from member details / heartbeat.
 */
export function usePromoExpiryPoll({ enabled, expiresAt, onForfeited }) {
	const onForfeitedRef = useRef(onForfeited);
	const inFlightRef = useRef(false);

	useEffect(() => {
		onForfeitedRef.current = onForfeited;
	}, [onForfeited]);

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}

		// No claim-expiry configured on this promo — nothing to poll.
		if (!expiresAt) {
			return undefined;
		}

		const expiresAtMs = Date.parse(expiresAt);
		if (!Number.isFinite(expiresAtMs)) {
			return undefined;
		}

		let cancelled = false;
		let timeoutId = null;

		const clearTimer = () => {
			if (timeoutId != null) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
		};

		const schedule = (delayMs) => {
			clearTimer();
			if (cancelled) {
				return;
			}
			timeoutId = setTimeout(tick, Math.max(0, Math.min(delayMs, MAX_WAIT_MS)));
		};

		const tick = async () => {
			if (cancelled || inFlightRef.current) {
				return;
			}
			inFlightRef.current = true;
			try {
				const responseJson = await ApiEngine.post(ApiUrl._API_PROMO_EXPIRY_TICK);
				if (cancelled) {
					return;
				}
				if (!responseJson?.[ApiKey._API_SUCCESS_KEY]) {
					schedule(POLL_INTERVAL_MS);
					return;
				}

				const data = responseJson[ApiKey._API_DATA_KEY] || {};
				const status = data.status;

				if (status === "forfeited") {
					if (typeof onForfeitedRef.current === "function") {
						onForfeitedRef.current(data);
					}
					return;
				}

				if (status === "none" || status === "claimable") {
					return;
				}

				if (status === "waiting" || status === "retry") {
					schedule(POLL_INTERVAL_MS);
					return;
				}

				// Still active: sleep until server expiresAt (or Redux value as fallback)
				if (status === "active" && data.expiresAt) {
					const nextMs = Date.parse(data.expiresAt);
					if (Number.isFinite(nextMs)) {
						const delay = nextMs - Date.now();
						schedule(delay <= 0 ? POLL_INTERVAL_MS : delay);
						return;
					}
				}

				// active with no claim expiry — stop
				if (status === "active") {
					return;
				}

				schedule(POLL_INTERVAL_MS);
			} catch (e) {
				if (!cancelled) {
					schedule(POLL_INTERVAL_MS);
				}
			} finally {
				inFlightRef.current = false;
			}
		};

		const delay = expiresAtMs - Date.now();
		schedule(delay <= 0 ? 0 : delay);

		return () => {
			cancelled = true;
			clearTimer();
		};
	}, [enabled, expiresAt]);
}
